import React, { useCallback, useEffect, useRef, useState } from 'react';
import { search } from '../api';
import { SearchIcon } from './icons';

/* Search is the way into the 21,000 series the front page deliberately does not
 * show. It stays a separate surface rather than a filter on the findings list:
 * the list is worth reading because it is short, and folding the universe into
 * it would bury the handful of things that actually moved. */

const KIND_LABEL = { company: 'Company', ticker: 'Ticker', brand: 'Brand', term: 'Term' };

export default function CommandBar({ open, onClose, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (open) {
      setQ('');
      setResults([]);
      setActive(0);
      // The input mounts with the overlay, so focus has to wait a frame.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 1) {
      setResults([]);
      return undefined;
    }
    // Every keystroke can outrun the one before it, so responses are stamped
    // and a stale one is dropped rather than overwriting a newer result.
    const mine = ++seq.current;
    setBusy(true);
    const timer = setTimeout(() => {
      search(q.trim(), 30)
        .then((data) => {
          if (mine !== seq.current) return;
          setResults(data);
          setActive(0);
        })
        .catch(() => {
          if (mine === seq.current) setResults([]);
        })
        .finally(() => {
          if (mine === seq.current) setBusy(false);
        });
    }, 120);
    return () => clearTimeout(timer);
  }, [q, open]);

  const choose = useCallback(
    (row) => {
      if (!row) return;
      onPick(row);
      onClose();
    },
    [onPick, onClose]
  );

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[active]);
    }
  };

  if (!open) return null;

  return (
    <div className="tw-cmd-backdrop" onMouseDown={onClose}>
      <div className="tw-cmd" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tw-cmd-input">
          <SearchIcon size={17} />
          <input
            ref={inputRef}
            value={q}
            placeholder="Ticker, company, brand or search term"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            autoComplete="off"
          />
          {busy && (
            <span className="tw-cmd-busy">
              <i />
              <i />
              <i />
            </span>
          )}
        </div>

        {results.length > 0 && (
          <div className="tw-cmd-results">
            {results.map((r, i) => (
              <button
                type="button"
                key={`${r.kind}-${r.company_id}-${r.series_id || 'x'}-${r.label}`}
                className={`tw-cmd-row${i === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(r)}
              >
                <span className="tw-cmd-kind">{KIND_LABEL[r.kind] || r.kind}</span>
                <span className="tw-cmd-label">{r.label}</span>
                <span className="tw-cmd-sub">{r.sublabel}</span>
              </button>
            ))}
          </div>
        )}

        {q.trim() && !busy && results.length === 0 && (
          <div className="tw-cmd-none">No match for “{q.trim()}”</div>
        )}

        <div className="tw-cmd-foot">
          <span>
            <span className="tw-kbd">↑</span>
            <span className="tw-kbd">↓</span>
            to move
          </span>
          <span>
            <span className="tw-kbd">Enter</span>
            to open
          </span>
          <span>
            <span className="tw-kbd">Esc</span>
            to close
          </span>
        </div>
      </div>
    </div>
  );
}
